import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Modal, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { fetchMeta } from '#action/meta/device';
import {
  fetchProjectDevices,
  fetchProject,
  setOpenPopup,
  setAddMode,
  setFormData as setFormDataAction,
  setDevices,
  clear,
  changePage as changePageAction,
  setFilter as setFilterAction,
  setDeviceType as setDeviceTypeAction,
  changeOrderAndOrderBy as changeOrderAndOrderByAction
} from '#action/project/devices-popup';
import {
  ModalBox,
  Header,
  HeaderTitle,
  Title,
  SubTitle,
  HeaderActions,
  Content,
  StyledCloseIcon,
  PaginationContainer,
  StyledPagination,
  PaginationCount
} from './styles';
import TableView from './devices-table-project';
import DevicesFilter from './components/filter/filter';
import CustomSelect from './components/select/select';
import DeleteDevicesButton from './components/action-buttons/delete-devices-button';
import AddDevicesButton from './components/action-buttons/add-devices-button';
import ToggleAddModeButton from './components/action-buttons/toggle-addMode-button';
import AddRemoveLink from './components/action-buttons/add-remove-link';
import { useDevicePopupChangesProject, useFloatDevicesSyncProject } from './hooks';
import { isUndefinedOrNull } from '#helper';

const DevicePopupProject = ({
  open,
  project: propsProject,
  handleClose,
  openedFromCreateForm,
  formData: formDevicesFromProps,
  user,
  formName = 'EditProjectForm',
  customComponents = {},
  customActions = {},
  data, // Additional form data
  disabled,
  alertId,
  setFormDevices,
  initialFormDevices,
  emptyForm,
  handleOpenPopupExternal
}) => {
  const dispatch = useDispatch();

  const {
    devices,
    page,
    pages,
    itemsPerPage,
    totalCount,
    status,
    filter,
    deviceType,
    project,
    addMode,
    isPopupStateChanged
  } = useSelector(s => s.projectDevicesPopup, shallowEqual);
  const projects = useSelector(s => s.account.directProjects, shallowEqual);
  const meta = useSelector(s => s.meta, shallowEqual);
  const theme = useTheme();
  const loading = ['idle', 'loading'].includes(status);
  const renderRef = useRef();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selected, setSelected] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState([]);
  const isUsersView = !isUndefinedOrNull(user);
  const title = isUsersView ? `${user.firstName} ${user.lastName}` : project?.title;
  const isRootProject = useMemo(
    () => Boolean(projects.find(p =>
      p.level === 0 && (p.id === project?.id || p.id === propsProject?.id))),
    [project?.id, projects, propsProject?.id]
  );
  const formDataObj = data ?? {
    formName,
    projectId: propsProject?.id,
    formDevices: formDevicesFromProps,
    initialFormDevices
  };
  const disabledByRootProject = isRootProject && !data;
  const showModeOnly = disabled || isUsersView || disabledByRootProject;
  const isAddDevicesButtonHidden = disabled || disabledByRootProject;
  // Pagination
  const from = (itemsPerPage * page) - itemsPerPage + 1;
  const to = (itemsPerPage * page) > totalCount ? totalCount : itemsPerPage * page;
  const projectId = isUsersView ? propsProject?.map(p => p.id).join(',') : propsProject?.id;

  const refetchDataRef = useRef();

  const components = {
    DeleteDevicesButton: customComponents.DeleteDevicesButton ?? DeleteDevicesButton,
    AddDevicesButton: customComponents.AddDevicesButton ?? AddDevicesButton,
    DevicesFilter: customComponents.DevicesFilter ?? DevicesFilter,
    CustomSelect: customComponents.CustomSelect ?? CustomSelect
  };

  const {
    DeleteDevicesButton: DeleteButton,
    AddDevicesButton: AddButton,
    DevicesFilter: Filter,
    CustomSelect: Select
  } = components;

  const actions = {
    fetchDevices: customActions.fetchDevices ?? fetchProjectDevices,
    setFilter: customActions.setFilter ?? setFilterAction,
    setDeviceType: customActions.setDeviceType ?? setDeviceTypeAction,
    changePage: customActions.changePage ?? changePageAction,
    changeOrderAndOrderBy: customActions.changeOrderAndOrderBy ?? changeOrderAndOrderByAction,
    setFormData: customActions.setFormData ?? setFormDataAction
  };
  const {
    fetchDevices,
    setFilter,
    setDeviceType,
    changePage,
    changeOrderAndOrderBy,
    setFormData
  } = actions;

  useDevicePopupChangesProject(formDataObj, openedFromCreateForm);
  useFloatDevicesSyncProject(
    formDataObj,
    openedFromCreateForm,
    addMode,
    renderRef,
    emptyForm,
    refetchDataRef
  );

  useEffect(() => {
    if (!Object.values(meta ?? {}).some(v => v.length)) {
      dispatch(fetchMeta());
    }
  }, [meta, dispatch]);

  const fetchData = useCallback(async () => {
    await dispatch(fetchProject(projectId));
    await dispatch(fetchDevices(projectId, addMode, openedFromCreateForm, data));
  }, [
    addMode,
    data,
    dispatch,
    fetchDevices,
    openedFromCreateForm,
    projectId
  ]);

  const fetchFormData = useCallback(async () => {
    await dispatch(setDevices(formDevicesFromProps, data));
    await dispatch(setFormData(formDevicesFromProps));
    await dispatch(
      fetchDevices(null, addMode, openedFromCreateForm, data)
    );
  }, [
    addMode,
    data,
    dispatch,
    fetchDevices,
    formDevicesFromProps,
    openedFromCreateForm,
    setFormData
  ]);

  // Only first time render.
  useEffect(() => {
    if (renderRef.current) return;
    renderRef.current = true;

    const init = async () => {
      // Seed the popup with the devices the form currently holds, so the fetch
      // below can tell which devices are already selected.
      await dispatch(setFormData(formDevicesFromProps ?? []));

      if (openedFromCreateForm && !projectId) {
        await fetchFormData();
      } else {
        await fetchData();
      }
    };

    init();
  }, [
    dispatch,
    setFormData,
    formDevicesFromProps,
    fetchFormData,
    fetchData,
    openedFromCreateForm,
    projectId
  ]);

  useEffect(() => {
    if (!refetchDataRef.current) {
      fetchData();
      refetchDataRef.current = false;
    }
  }, [fetchData, refetchDataRef]);

  useEffect(() => {
    setSelected([]);
    setSelectedAlert([]);
  }, [filter, page, deviceType]);

  const handlePageChange = useCallback(async (event, newPage) => {
    if (page !== newPage) {
      dispatch(changePage(newPage, projectId, addMode, openedFromCreateForm, data));
    }
  }, [dispatch, changePage, page, projectId, addMode, openedFromCreateForm, data]);

  const handleClosePopup = async () => {
    dispatch(setOpenPopup(!open));
    handleClose?.();

    dispatch(clear());
    handleOpenPopupExternal?.(false);
  };

  const handleAddMode = () => {
    setSelected([]);
    dispatch(setAddMode(!addMode));

    dispatch(fetchDevices(propsProject?.id, !addMode, openedFromCreateForm, data));
  };

  return (
    <Modal
      open={open}
      onClose={handleClosePopup}
      aria-labelledby="Devices popup"
      data-loading={loading ? 'true' : 'false'}
    >
      <ModalBox>
        <StyledCloseIcon onClick={handleClosePopup} />
        <Header>
          <HeaderTitle>
            <Title variant="h4">
              Devices
            </Title>
            <SubTitle component="h6">
              <span>{title}</span>
            </SubTitle>
          </HeaderTitle>
          <HeaderActions>
            <Filter
              projectId={projectId}
              addMode={addMode}
              openedFromCreateForm={openedFromCreateForm}
              data={data}
              setFilter={setFilter}
              changePage={changePage}
            />
            {!isMobile && (
              (data && !data?.userId) ? <div /> : <Select
                meta={meta}
                projectId={projectId}
                addMode={addMode}
                openedFromCreateForm={openedFromCreateForm}
                data={data}
                setDeviceType={setDeviceType}
              />
            )}
            {isUsersView ?
              <AddRemoveLink
                propsProject={propsProject}
              /> : isAddDevicesButtonHidden ? null :
              <ToggleAddModeButton
                addMode={addMode}
                handleAddMode={handleAddMode}
              />}
          </HeaderActions>
        </Header>
        <Content showChangesLabel={isPopupStateChanged}>
          <TableView
            meta={meta}
            devices={devices}
            loading={loading}
            dispatch={dispatch}
            projectId={projectId}
            selected={selected}
            setSelected={setSelected}
            selectedAlert={selectedAlert}
            setSelectedAlert={setSelectedAlert}
            addMode={addMode}
            openedFromCreateForm={openedFromCreateForm}
            showModeOnly={showModeOnly}
            data={data}
            changeOrderAndOrderBy={changeOrderAndOrderBy}
            alertId={alertId}
            showChangesLabel={isPopupStateChanged}
            setFormDevices={setFormDevices}
            formDevices={formDevicesFromProps}
          />
          {(selected?.length && !addMode) ?
            <DeleteButton
              selected={selected}
              setSelected={setSelected}
              project={project}
              addMode={addMode}
              openedFromCreateForm={openedFromCreateForm}
              data={data}
              fetchDevices={fetchDevices}
              selectedAlert={selectedAlert}
              alertId={alertId}
              formDataObj={formDataObj}
              setFormDevices={setFormDevices}
              formDevices={formDevicesFromProps}
            /> : null}
          {(selected?.length && addMode) ?
            <AddButton
              selected={selected}
              setSelected={setSelected}
              project={project}
              addMode={addMode}
              data={data}
              openedFromCreateForm={openedFromCreateForm}
              fetchDevices={fetchDevices}
              selectedAlert={selectedAlert}
              setSelectedAlert={setSelectedAlert}
              alertId={alertId}
              formDataObj={formDataObj}
              setFormDevices={setFormDevices}
              formDevices={formDevicesFromProps}
            /> : null}
        </Content>
        <PaginationContainer>
          {(!!pages && Number(pages) > 1) && (
          <StyledPagination
            shape="rounded"
            color="secondary"
            count={pages}
            page={page}
            onChange={handlePageChange}
          />)}
          {(devices?.length > 0 && totalCount) && !loading ?
            <PaginationCount>{`${from} - ${to} out of ${totalCount}`}</PaginationCount> : null}
        </PaginationContainer>
      </ModalBox>
    </Modal>
  );
};

export default DevicePopupProject;
